import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Users, Package, ArrowLeftRight, MessageSquare, Coins, TrendingUp, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
      if (showToast) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const { overview, recent_users, user_growth, transaction_stats, top_providers, recent_transactions } = stats;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-6">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Monitor your Equonomy platform</p>
          </div>
          <Button 
            onClick={() => fetchStats(true)} 
            disabled={refreshing}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview.total_users}</div>
              <p className="text-xs text-blue-100 mt-1">Registered accounts</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="w-4 h-4" />
                Services
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview.active_services}</div>
              <p className="text-xs text-purple-100 mt-1">{overview.total_services} total listings</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4" />
                Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview.completed_transactions}</div>
              <p className="text-xs text-green-100 mt-1">{overview.total_transactions} total</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Coins className="w-4 h-4" />
                Token Circulation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview.total_tokens_circulation}</div>
              <p className="text-xs text-orange-100 mt-1">{overview.total_escrow_tokens} in escrow</p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                Communication
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Messages</span>
                  <span className="font-bold text-xl">{overview.total_messages}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Active Conversations</span>
                  <span className="font-bold text-xl">{overview.total_conversations}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Transaction Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {transaction_stats.length > 0 ? (
                  transaction_stats.map((stat) => (
                    <div key={stat._id} className="flex justify-between items-center">
                      <span className="text-gray-600 capitalize">{stat._id}</span>
                      <span className="font-semibold">{stat.count}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No transactions yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Growth Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              User Growth (Last 7 Days)
            </CardTitle>
            <CardDescription>New user registrations per day</CardDescription>
          </CardHeader>
          <CardContent>
            {user_growth && user_growth.length > 0 ? (
              <div className="flex items-end justify-between gap-2 h-40">
                {user_growth.map((day, idx) => {
                  const maxCount = Math.max(...user_growth.map(d => d.count), 1);
                  const height = (day.count / maxCount) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full bg-purple-200 rounded-t relative group cursor-pointer" 
                           style={{ height: `${height || 5}%`, minHeight: '10px' }}>
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {day.count} users
                        </div>
                      </div>
                      <span className="text-xs text-gray-600 transform -rotate-45 origin-top-left mt-4">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No user growth data available</p>
            )}
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Users */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Users</CardTitle>
              <CardDescription>Latest 10 registered users</CardDescription>
            </CardHeader>
            <CardContent>
              {recent_users && recent_users.length > 0 ? (
                <div className="space-y-3">
                  {recent_users.map((user) => (
                    <div key={user.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="font-semibold text-gray-900">{user.username}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(user.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-purple-600">{user.time_tokens} tokens</p>
                        {user.escrow_tokens > 0 && (
                          <p className="text-xs text-gray-500">{user.escrow_tokens} in escrow</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No users yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Service Providers */}
          <Card>
            <CardHeader>
              <CardTitle>Top Service Providers</CardTitle>
              <CardDescription>Users with most active services</CardDescription>
            </CardHeader>
            <CardContent>
              {top_providers && top_providers.length > 0 ? (
                <div className="space-y-3">
                  {top_providers.map((provider, idx) => (
                    <div key={provider._id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">
                          {idx + 1}
                        </div>
                        <p className="font-semibold text-gray-900">{provider.username}</p>
                      </div>
                      <div className="text-purple-600 font-semibold">
                        {provider.service_count} services
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No service providers yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
            <CardDescription>Latest 10 transactions on the platform</CardDescription>
          </CardHeader>
          <CardContent>
            {recent_transactions && recent_transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="pb-3 font-semibold text-gray-700">From</th>
                      <th className="pb-3 font-semibold text-gray-700">To</th>
                      <th className="pb-3 font-semibold text-gray-700">Tokens</th>
                      <th className="pb-3 font-semibold text-gray-700">Status</th>
                      <th className="pb-3 font-semibold text-gray-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent_transactions.map((txn) => (
                      <tr key={txn.id} className="border-b last:border-0">
                        <td className="py-3 text-gray-900">{txn.from_username}</td>
                        <td className="py-3 text-gray-900">{txn.to_username}</td>
                        <td className="py-3 font-semibold text-purple-600">{txn.time_tokens}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            txn.status === 'completed' ? 'bg-green-100 text-green-700' :
                            txn.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            txn.status === 'in_escrow' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {txn.status}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-gray-600">
                          {new Date(txn.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No transactions yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminDashboard;
